/**
 * Dynamic Metadata Management
 *
 * Utilities for updating and managing dynamic metadata for SEO and social sharing.
 */

// Import from our single source of truth
import { APP_VERSION, BUILD_ID } from './versionManager';

export interface PageMetadata {
  title?: string;
  description?: string;
  image?: string;
  type?: string;
  siteName?: string;
  url?: string;
  timestamp?: number;
  path?: string;
}

// Default metadata for different routes
const routeMetadata: { [path: string]: PageMetadata } = {
  '/': {
    title: "Selah - Your Digital Bible Companion",
    description: "Explore Scripture, generate personalized prayers, and strengthen your faith with Selah - your AI-powered Bible companion.",
    image: "https://bbhhusjprzalcwvkgvsa.supabase.co/storage/v1/object/public/photos//text%20and%20social.png?v=" + APP_VERSION,
    type: "website",
    siteName: "Selah"
  },
  '/about': {
    title: "About Selah - Our Mission and Vision",
    description: "Learn about Selah's mission to help you explore Scripture, generate personalized prayers, and strengthen your faith with our AI-powered Bible companion.",
    type: "website",
    siteName: "Selah"
  },
  '/atheism-defense': {
    title: "Selah | Atheism Defense",
    description: "Equipping believers with biblical answers to common objections raised by atheists.",
    type: "website",
    siteName: "Selah"
  },
  '/interfaith-dialogue': {
    title: "Selah | Interfaith Dialogue",
    description: "Explore Christian responses to other faiths, promoting understanding and informed dialogue.",
    type: "website",
    siteName: "Selah"
  },
  '/find-your-verse': {
    title: "Selah | Find Your Verse",
    description: "Discover Bible verses that speak to your current needs, questions, or circumstances.",
    type: "website",
    siteName: "Selah"
  },
  '/personalized-prayer': {
    title: "Selah | Personalized Prayer",
    description: "Generate personalized prayers based on your needs and circumstances, deepening your connection with God.",
    type: "website",
    siteName: "Selah"
  },
  '/dashboard': {
    title: "Selah | Dashboard",
    description: "Your personal dashboard to manage saved content and track your spiritual growth with Selah.",
    type: "website",
    siteName: "Selah"
  },
  '/privacy': {
    title: "Selah | Privacy Policy",
    description: "Read the privacy policy for Selah, detailing how we protect your data and personal information.",
    type: "website",
    siteName: "Selah"
  },
  '/terms': {
    title: "Selah | Terms of Service",
    description: "Review the terms of service for using Selah, outlining the rules and guidelines for our platform.",
    type: "website",
    siteName: "Selah"
  },
  '/contact': {
    title: "Contact Selah",
    description: "Contact Selah for support, questions, or feedback. We're here to help you on your spiritual journey.",
    type: "website",
    siteName: "Selah"
  },
  '/404': {
    title: "Selah | 404 Not Found",
    description: "Oops! The page you're looking for can't be found. Return to our homepage to continue exploring Selah.",
    type: "website",
    siteName: "Selah"
  },
  '/500': {
    title: "Selah | 500 Internal Server Error",
    description: "We encountered an internal server error. Please try again later or contact support if the issue persists.",
    type: "website",
    siteName: "Selah"
  }
};

/**
 * Get default metadata for a given route
 * @param path The route path
 * @returns PageMetadata The default metadata for the route
 */
export const getRouteMetadata = (path: string): PageMetadata => {
  // Remove trailing slash if present
  const cleanPath = path.endsWith('/') && path.length > 1 ? path.slice(0, -1) : path;
  
  // Check if the cleanPath exists in routeMetadata
  if (routeMetadata[cleanPath]) {
    return routeMetadata[cleanPath];
  }
  
  // Fallback to the root metadata if no specific route is found
  return routeMetadata['/'];
};

/**
 * Update all metadata tags in the document head
 * @param metadata The metadata to update
 */
export const updateAllMetadata = (metadata: PageMetadata = {}): void => {
  // Default values
  const title = metadata.title || "Selah - Your Digital Bible Companion";
  const description = metadata.description || "Explore Scripture, generate personalized prayers, and strengthen your faith with Selah - your AI-powered Bible companion.";
  const image = metadata.image || "https://bbhhusjprzalcwvkgvsa.supabase.co/storage/v1/object/public/photos//text%20and%20social.png?v=" + APP_VERSION;
  const type = metadata.type || "website";
  const siteName = metadata.siteName || "Selah";
  const url = metadata.url || "https://selahtools.com";
  
  // Update title tag
  document.title = title;
  
  // Update meta tags
  updateMetaTag('description', description);
  updateMetaTag('og:title', title);
  updateMetaTag('og:description', description);
  updateMetaTag('og:image', image);
  updateMetaTag('og:type', type);
  updateMetaTag('og:site_name', siteName);
  updateMetaTag('og:url', url);
  updateMetaTag('twitter:title', title);
  updateMetaTag('twitter:description', description);
  updateMetaTag('twitter:image', image);
  updateMetaTag('twitter:card', 'summary_large_image');
  updateMetaTag('twitter:site', '@selahtools');
  updateMetaTag('twitter:creator', '@selahtools');
  updateMetaTag('application-name', siteName);
  updateMetaTag('apple-mobile-web-app-title', siteName);
  updateMetaTag('msapplication-TileColor', '#ffffff');
  updateMetaTag('theme-color', '#ffffff');
  updateMetaTag('app-version', BUILD_ID);
  updateMetaTag('app-build-time', new Date().toISOString());
  
  // Update canonical URL
  updateCanonicalURL(url);
};

/**
 * Update a specific meta tag
 * @param property The property of the meta tag
 * @param content The content to set
 */
const updateMetaTag = (property: string, content: string): void => {
  let metaTag: HTMLMetaElement | null = document.querySelector(`meta[property="${property}"]`) || document.querySelector(`meta[name="${property}"]`);
  
  if (!metaTag) {
    metaTag = document.createElement('meta');
    
    if (property.startsWith('og:') || property === 'application-name') {
      metaTag.setAttribute('property', property);
    } else {
      metaTag.setAttribute('name', property);
    }
    
    document.head.appendChild(metaTag);
  }
  
  metaTag.setAttribute('content', content);
};

/**
 * Update the canonical URL
 * @param url The URL to set
 */
const updateCanonicalURL = (url: string): void => {
  let canonicalLink: HTMLLinkElement | null = document.querySelector('link[rel="canonical"]');
  
  if (!canonicalLink) {
    canonicalLink = document.createElement('link');
    canonicalLink.setAttribute('rel', 'canonical');
    document.head.appendChild(canonicalLink);
  }
  
  canonicalLink.setAttribute('href', url);
};

/**
 * Try to refresh Facebook's cache for the current URL
 */
export const refreshFacebookCache = (): void => {
  try {
    // Check if Facebook SDK is available
    if (typeof window !== 'undefined' && typeof window.FB !== 'undefined') {
      // Force Facebook to scrape the page again
      window.FB.XFBML.parse();
    }
  } catch (e) {
    console.error('Error refreshing Facebook cache:', e);
  }
};

/**
 * Clean up any service worker registrations to prevent cache issues
 */
export const unregisterServiceWorkers = async (): Promise<boolean> => {
  if ('serviceWorker' in navigator) {
    try {
      const registrations = await navigator.serviceWorker.getRegistrations();
      for (const registration of registrations) {
        await registration.unregister();
      }
      return true;
    } catch (error) {
      console.error('Service worker unregistration failed:', error);
      return false;
    }
  }
  return false;
};
